import type { Frame } from "../api/types";

export interface FrameLightboxProps {
  frame: Frame;
  /** Resolved presigned R2 URL for the full-res frame, or null while loading. */
  fullUrl: string | null;
  /**
   * Resolve a FRESH presigned URL at download-click time. Presigned R2 GET
   * URLs are short-lived (~5 min); once expired, R2 answers the GET with a 403
   * that carries no `Access-Control-Allow-Origin`, so the browser turns the
   * cross-origin fetch into a bare `TypeError: Failed to fetch`. Re-signing on
   * click keeps the URL valid for the (millisecond-long) fetch. Falls back to
   * `fullUrl` when omitted.
   */
  resolveDownloadUrl?: () => Promise<string>;
  /**
   * Filename for the downloaded JPEG. Defaults to `<frameId>.jpg`; callers pass
   * a human name (e.g. `frame_18_39_02.jpg`) to match the viewer's capture-time
   * download name.
   */
  downloadFilename?: string;
  myReviewerId: string;
  /** When true, show the Delete affordance even on frames captured by others. */
  isAdmin?: boolean;
  onClose: () => void;
  onDelete: (frameId: string) => void;
  onJumpToVideo: (videoTimeS: number) => void;
}

async function downloadFromCrossOriginUrl(url: string, filename: string): Promise<void> {
  // Cross-origin <a download> is ignored by browsers — fetch the bytes,
  // wrap in a same-origin blob URL, then trigger a download click.
  const res = await fetch(url);
  if (!res.ok) throw new Error(`download failed: ${res.status}`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

export function FrameLightbox({
  frame, fullUrl, resolveDownloadUrl, downloadFilename, myReviewerId, isAdmin = false,
  onClose, onDelete, onJumpToVideo,
}: FrameLightboxProps): JSX.Element {
  const canDelete = frame.capturedBy.reviewerId === myReviewerId || isAdmin;
  const handleDownload = async (): Promise<void> => {
    if (fullUrl === null) return;
    try {
      const url = resolveDownloadUrl !== undefined ? await resolveDownloadUrl() : fullUrl;
      await downloadFromCrossOriginUrl(url, downloadFilename ?? `${frame.id}.jpg`);
    } catch (err) {
      const msg = (err as Error).message;
      // A bare "Failed to fetch" almost always means the (cross-origin) image
      // request was blocked before a response — typically an expired link or a
      // network/CORS block — so give the user something actionable.
      alert(
        /failed to fetch/i.test(msg)
          ? "Download failed — the link may have expired or the network blocked it. Please try again."
          : `Download failed: ${msg}`,
      );
    }
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.85)",
        display: "flex", flexDirection: "column", zIndex: 1000,
      }}
    >
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {fullUrl === null ? (
          <span style={{ color: "#bbb" }}>Loading…</span>
        ) : (
          <img src={fullUrl} alt="frame" style={{ maxWidth: "95%", maxHeight: "85vh" }} />
        )}
      </div>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", padding: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}
      >
        <span>Captured by {frame.capturedBy.displayName} at {new Date(frame.capturedAt).toLocaleString()}</span>
        <button type="button" onClick={() => onJumpToVideo(frame.videoTimeS)}>
          {frame.source === "panorama" ? "🌐 Jump to panorama" : "⏯ Jump to video"}
        </button>
        <button
          type="button"
          onClick={() => { void handleDownload(); }}
          disabled={fullUrl === null}
        >
          ⬇ Download original
        </button>
        {frame.poiId !== undefined && <span>🔗 Linked to POI {frame.poiId}</span>}
        {canDelete && (
          <button type="button" onClick={() => { if (confirm("Delete this frame?")) onDelete(frame.id); }}>
            🗑 Delete
          </button>
        )}
        <button type="button" onClick={onClose} style={{ marginLeft: "auto" }}>Close</button>
      </div>
    </div>
  );
}
